import { Controller, useForm, useFormContext } from 'react-hook-form';
import { CustomInput, DatePicker, Input, UISelect } from '../common/forms';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { errorMessage, successMessage } from '~/utils/toasts';

import Axios from '~/utils/axios';
import { DateTime } from 'luxon';
import InputMask from 'react-input-mask';
import { RequestFooter } from './request-footer';
import { sentryCaptureException } from '~/errors/sentry';
import { useRouter } from 'next/router';
import { renderErrorsToToaster } from './render-error-to-toaster';
import { useGetVisaRequestsDetails } from '~/apis/dhamen';

export const ApplicantInformation = ({ nationalitiesList, countriesList }: any) => {
   const { translate, lang } = useTranslate();
   const router = useRouter();
   const [loading, setLoading] = useState(false);
   const { id, applicationNo } = router.query;
   const isEdit = Boolean(applicationNo);
   const { reset, getValues } = useFormContext<any>();
   const birthDate = getValues('birthDay');
   const formValues = getValues();
   const [isArabNationality, setIsArabNationality] = useState(false);
   const [disabled, setDisabled] = useState(false);
   const { data: details } = useGetVisaRequestsDetails((id as string) ?? '');
   const visitors = details?.visitors?.length ?? 0;
   const minBirthDate = DateTime.local().minus({ years: 18 }).toISODate() as any;
   const {
      register,
      control,
      formState: { errors },
      handleSubmit,
      watch,
   } = useForm<any>({
      defaultValues: {
         ...formValues,
      },
   });
   const religion = watch('religion');

   const handleNationalityChange = (selectedValue: string) => {
      const selectedOption = nationalitiesList.find((opt: any) => opt.value === selectedValue);
      if (selectedOption) {
         setIsArabNationality(selectedOption.arabNationality);
      } else {
         setIsArabNationality(false);
      }
   };

   const handleNonMuslim = () => {
      if (religion === 'OTHERS' && details?.visitPurpose === 'UMRAH') {
         setDisabled(true);
         errorMessage(translate({ id: 'visasManager:non muslim' }));
      } else setDisabled(false);
   };

   useEffect(() => {
      handleNonMuslim();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [religion]);

   const ReligionOptions = [
      {
         value: 'MUSLIM',
         label: translate({ id: 'visasManager:muslim' }),
      },
      {
         value: 'OTHERS',
         label: translate({ id: 'visasManager:others' }),
      },
   ];

   const CompanionTypeOptions = [
      {
         value: 'SON',
         label: translate({ id: 'visasManager:son' }),
      },
      {
         value: 'FATHER',
         label: translate({ id: 'visasManager:father' }),
      },
      {
         value: 'BROTHER',
         label: translate({ id: 'visasManager:brother' }),
      },
      {
         value: 'HUSBAND',
         label: translate({ id: 'visasManager:husband' }),
      },
      {
         value: 'GRANDPA',
         label: translate({ id: 'visasManager:grandpa' }),
      },
      {
         value: 'NEPHEW',
         label: translate({ id: 'visasManager:nephew' }),
      },
      {
         value: 'GRANDSON',
         label: translate({ id: 'visasManager:grandson' }),
      },
      {
         value: 'UNCLE',
         label: translate({ id: 'visasManager:uncle' }),
      },
      {
         value: 'DAUGHTER_HUSBAND',
         label: translate({ id: 'visasManager:daughter husband' }),
      },
      {
         value: 'STEPFATHER',
         label: translate({ id: 'visasManager:stepfather' }),
      },
      {
         value: 'FATHER_IN_LAW',
         label: translate({ id: 'visasManager:father in law' }),
      },
   ];

   const CompanionList = useMemo(() => {
      if (!details?.visitors?.length) return [];
      return details?.visitors
         ?.filter((item: { companionApplicationNo: string }) => !item?.companionApplicationNo)
         ?.map((visitor: { applicationNo: string; firstNameEn: string; familyNameEn: string }) => ({
            value: visitor?.applicationNo,
            label: `${visitor?.firstNameEn} ${visitor?.familyNameEn}`,
         }));
   }, [details?.visitors]);

   const calculateAge = useCallback(
      (birthDate: string) => {
         const today = new Date();
         const birthDateArr = birthDate?.split('-');
         const birthYear = parseInt(birthDateArr?.[0], 10);
         const birthMonth = parseInt(birthDateArr?.[1], 10);
         const birthDay = parseInt(birthDateArr?.[2], 10);
         let ageDiff = today.getFullYear() - birthYear;

         if (
            today.getMonth() < birthMonth - 1 ||
            (today.getMonth() === birthMonth - 1 && today.getDate() < birthDay)
         ) {
            ageDiff--;
         }
         return ageDiff;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [birthDate]
   );

   const [underAge, setUnderAge] = useState<boolean>(
      birthDate && calculateAge(String(birthDate)) < 18 ? true : false
   );

   const submitForm = async (values: any) => {
      //applicationNo
      setLoading(true);
      // if (underAge) {
      //    setValue('companionType', '');
      //    setValue('companionApplicationNo', '');
      // }
      const body = {
         visitors: [{ ...values }],
         current_step: 'applicant_information',
      };
      try {
         if (isEdit) {
            await Axios.put(`/dhamen/v1/visa-request/${id}/visitors/${applicationNo}`, body);
            reset({ ...getValues(), ...values });
            successMessage(translate({ id: 'visasManager:submit message' }));
            router.push(
               `/${lang}/umrah-requests/${id}/add-applicant/${applicationNo}/edit/visa-information`
            );
         } else {
            const { data } = await Axios.patch(`/dhamen/v1/visa-request/${id}`, body);
            if (data?.status.toLowerCase() === 'success' && data.applicationNo) {
               reset({ ...getValues(), ...values });
               successMessage(translate({ id: 'visasManager:submit message' }));
               router.push(
                  `/${lang}/umrah-requests/${id}/add-applicant/${data.applicationNo}/edit/visa-information`
               );
            } else throw new Error(data?.error || data?.original?.error || 'Something went wrong');
         }
      } catch (error: any) {
         console.log(error);
         sentryCaptureException(error);
         renderErrorsToToaster(error?.response?.data);
      }
      setLoading(false);
   };

   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:applicant information" />
            </div>

            <div className="w-full">
               <form onSubmit={handleSubmit(submitForm)}>
                  <div className="mb-5">
                     <Controller
                        name="nationality"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select nationality',
                              })}
                              label={translate({
                                 id: 'visasManager:nationality',
                              })}
                              id="nationality"
                              value={nationalitiesList?.find((opt: any) => opt.value === value)}
                              options={nationalitiesList}
                              error={errors.nationality?.message}
                              onChange={({ value }: any) => {
                                 onChange(value);
                                 handleNationalityChange(value);
                              }}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <div className="pt-3 font-semibold">
                        <Translate id="visasManager:title" /> <span className="text-red">*</span>
                     </div>
                     <div className="flex flex-wrap w-full py-2 gap-y-5 gap-x-12">
                        <Controller
                           name="title"
                           control={control}
                           render={() => (
                              <label className="flex items-center space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="MR"
                                    {...register('title', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                    defaultChecked
                                 />
                                 <span>
                                    <Translate id="visasManager:mr" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="title"
                           control={control}
                           render={() => (
                              <label className="flex items-center space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="MRS"
                                    {...register('title', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:mrs" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="title"
                           control={control}
                           render={() => (
                              <label className="flex items-center space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="HIS_EXCELLENCY"
                                    {...register('title', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:his excellency" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="title"
                           control={control}
                           render={() => (
                              <label className="flex items-center space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="MISS"
                                    {...register('title', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:miss" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="title"
                           control={control}
                           render={() => (
                              <label className="flex items-center space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="DR"
                                    {...register('title', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:dr" />
                                 </span>
                              </label>
                           )}
                        />
                     </div>
                  </div>
                  <div className="mb-5">
                     <Input
                        id="firstNameEn"
                        label={<Translate id="visasManager:first name en" />}
                        placeholder={translate({ id: 'visasManager:enter first name en' })}
                        isRequired
                        error={errors.firstNameEn?.message}
                        {...register('firstNameEn', {
                           required: translate({ id: 'validation:required' }),
                           pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="flex flex-col items-start gap-5 mb-5 lg:flex-row">
                     <Input
                        id="middleNameEn"
                        label={<Translate id="visasManager:middle name en" />}
                        placeholder={translate({ id: 'visasManager:enter middle name en' })}
                        error={errors.middleNameEn?.message}
                        {...register('middleNameEn', {
                           pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        })}
                        groupClassName="w-full"
                     />
                     <Input
                        id="grandFatherNameEn"
                        label={<Translate id="visasManager:grandfather name en" />}
                        placeholder={translate({ id: 'visasManager:enter grandfather name en' })}
                        error={errors.grandFatherNameEn?.message}
                        {...register('grandFatherNameEn', {
                           pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        })}
                        groupClassName="w-full"
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="familyNameEn"
                        label={<Translate id="visasManager:family name en" />}
                        placeholder={translate({ id: 'visasManager:enter family name en' })}
                        isRequired
                        error={errors.familyNameEn?.message}
                        {...register('familyNameEn', {
                           required: translate({ id: 'validation:required' }),
                           pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="email"
                        label={<Translate id="common:email address" />}
                        placeholder={translate({ id: 'common:email address placeholder' })}
                        isRequired
                        type="email"
                        error={errors.email?.message}
                        {...register('email', {
                           required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 50,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(50),
                                 },
                              }),
                           },
                           pattern: {
                              value: /^[A-Za-z0-9@._%+-]+$/i,
                              message: translate({ id: 'validation:english letters only' }),
                           },
                        })}
                        description={<Translate id="visasManager:email address description" />}
                     />
                  </div>
                  {isArabNationality ? (
                     <>
                        <div className="mb-5">
                           <Input
                              id="firstNameAr"
                              label={<Translate id="visasManager:first name ar" />}
                              placeholder={translate({ id: 'visasManager:enter first name ar' })}
                              isRequired={isArabNationality ? true : false}
                              error={errors.firstNameAr?.message}
                              {...register('firstNameAr', {
                                 required: isArabNationality
                                    ? translate({ id: 'validation:required' })
                                    : false,
                                 pattern: {
                                    value: /^[\u0600-\u06FF\s]+$/,
                                    message: translate({ id: 'validation:arabic letters only' }),
                                 },
                                 maxLength: {
                                    value: 15,
                                    message: translate({
                                       id: 'validation:max characters',
                                       values: {
                                          count: String(15),
                                       },
                                    }),
                                 },
                              })}
                           />
                        </div>
                        <div className="flex flex-col items-start gap-5 mb-5 lg:flex-row">
                           <Input
                              id="middleNameAr"
                              label={<Translate id="visasManager:middle name ar" />}
                              placeholder={translate({ id: 'visasManager:enter middle name ar' })}
                              error={errors.middleNameAr?.message}
                              {...register('middleNameAr', {
                                 pattern: {
                                    value: /^[\u0600-\u06FF\s]+$/,
                                    message: translate({ id: 'validation:arabic letters only' }),
                                 },
                                 maxLength: {
                                    value: 15,
                                    message: translate({
                                       id: 'validation:max characters',
                                       values: {
                                          count: String(15),
                                       },
                                    }),
                                 },
                              })}
                              groupClassName="w-full"
                           />
                           <Input
                              id="grandFatherNameAr"
                              label={<Translate id="visasManager:grandfather name ar" />}
                              placeholder={translate({
                                 id: 'visasManager:enter grandfather name ar',
                              })}
                              error={errors.grandFatherNameAr?.message}
                              {...register('grandFatherNameAr', {
                                 pattern: {
                                    value: /^[\u0600-\u06FF\s]+$/,
                                    message: translate({ id: 'validation:arabic letters only' }),
                                 },
                                 maxLength: {
                                    value: 15,
                                    message: translate({
                                       id: 'validation:max characters',
                                       values: {
                                          count: String(15),
                                       },
                                    }),
                                 },
                              })}
                              groupClassName="w-full"
                           />
                        </div>
                        <div className="mb-5">
                           <Input
                              id="familyNameAr"
                              label={<Translate id="visasManager:family name ar" />}
                              placeholder={translate({ id: 'visasManager:enter family name ar' })}
                              isRequired={isArabNationality ? true : false}
                              error={errors.familyNameAr?.message}
                              {...register('familyNameAr', {
                                 required: isArabNationality
                                    ? translate({ id: 'validation:required' })
                                    : false,
                                 pattern: {
                                    value: /^[\u0600-\u06FF\s]+$/,
                                    message: translate({ id: 'validation:arabic letters only' }),
                                 },
                                 maxLength: {
                                    value: 15,
                                    message: translate({
                                       id: 'validation:max characters',
                                       values: {
                                          count: String(15),
                                       },
                                    }),
                                 },
                              })}
                           />
                        </div>
                     </>
                  ) : null}
                  <div className="mb-5">
                     <Controller
                        name="religion"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select religion',
                              })}
                              label={translate({
                                 id: 'visasManager:religion',
                              })}
                              id="religion"
                              value={ReligionOptions.find(opt => opt.value === value)}
                              options={ReligionOptions}
                              error={errors.religion?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <div className="pt-3 font-semibold">
                        <Translate id="visasManager:gender" /> <span className="text-red">*</span>
                     </div>
                     <div className="flex gap-4 py-2">
                        <Controller
                           name="gender"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="MALE"
                                    {...register('gender', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                    defaultChecked
                                 />
                                 <span>
                                    <Translate id="common:male" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="gender"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="FEMALE"
                                    {...register('gender', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="common:female" />
                                 </span>
                              </label>
                           )}
                        />
                     </div>
                  </div>
                  <div className="mb-5">
                     <Controller
                        control={control}
                        name="birthDay"
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <DatePicker
                              placeholderText={translate({
                                 id: 'common:date',
                              })}
                              id="birthDay"
                              maxDate={
                                 visitors == 0
                                    ? DateTime.fromFormat(minBirthDate, 'yyyy-MM-dd').toJSDate()
                                    : new Date()
                              }
                              label={translate({ id: 'visasManager:birthdate' })}
                              value={value?.split?.('T')?.[0] || value}
                              error={errors.birthDay?.message}
                              onChange={val => {
                                 const d = DateTime.fromJSDate(val as Date);
                                 onChange(d.toFormat('yyyy-MM-dd'));
                                 const age = calculateAge(String(d.toFormat('yyyy-MM-dd')));
                                 if (age < 18) {
                                    setUnderAge(true);
                                 } else {
                                    setUnderAge(false);
                                 }
                              }}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  {underAge ? (
                     <>
                        <div className="mb-5">
                           <Controller
                              name="companionType"
                              control={control}
                              rules={{ required: translate({ id: 'validation:required' }) }}
                              render={({ field: { onChange, value } }) => (
                                 <UISelect
                                    placeholder={translate({
                                       id: 'visasManager:select companion type',
                                    })}
                                    label={translate({
                                       id: 'visasManager:companion type',
                                    })}
                                    id="companionType"
                                    defaultValue={CompanionTypeOptions.find(
                                       opt => opt.value === value
                                    )}
                                    options={CompanionTypeOptions}
                                    error={errors.companionType?.message}
                                    onChange={({ value }: any) => onChange(value)}
                                    isRequired
                                 />
                              )}
                           />
                        </div>
                        <div className="mb-5">
                           {CompanionList?.length ? (
                              <Controller
                                 name="companionApplicationNo"
                                 control={control}
                                 rules={{ required: translate({ id: 'validation:required' }) }}
                                 render={({ field: { onChange, value } }) => (
                                    <UISelect
                                       label={translate({
                                          id: 'visasManager:select companion',
                                       })}
                                       placeholder={translate({
                                          id: 'visasManager:select companion',
                                       })}
                                       id="companionApplicationNo"
                                       defaultValue={CompanionList.find(
                                          (opt: { value: string }) => opt.value === value
                                       )}
                                       options={CompanionList}
                                       error={errors.companionType?.message}
                                       onChange={({ value }: any) => onChange(value)}
                                       isRequired
                                    />
                                 )}
                              />
                           ) : (
                              <Controller
                                 control={control}
                                 name="companionApplicationNo"
                                 rules={{
                                    validate: value =>
                                       value > 0 || translate({ id: 'validation:required' }),
                                 }}
                                 render={({ field: { onBlur, onChange, ref, value } }) => (
                                    <InputMask
                                       mask="9999999999999999999"
                                       maskChar=""
                                       onChange={onChange}
                                       onBlur={onBlur}
                                       defaultValue={value}>
                                       {(props: any) => (
                                          <CustomInput
                                             {...props}
                                             defaultValue={value}
                                             inputMode="numeric"
                                             id="companionApplicationNo"
                                             label={translate({
                                                id: 'visasManager:companion application number',
                                             })}
                                             placeholder={translate({
                                                id: 'visasManager:enter companion application number',
                                             })}
                                             isRequired
                                             error={errors.companionApplicationNo?.message}
                                             ref={ref}
                                          />
                                       )}
                                    </InputMask>
                                 )}
                              />
                           )}
                        </div>
                     </>
                  ) : null}
                  <div className="mb-5">
                     <Input
                        id="birthPlace"
                        label={translate({
                           id: 'visasManager:birthplace',
                        })}
                        isRequired
                        error={errors.birthPlace?.message}
                        {...register('birthPlace', {
                           required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="mb-5">
                     <Controller
                        name="birthCountry"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select birth country',
                              })}
                              label={translate({
                                 id: 'visasManager:birth country',
                              })}
                              id="birthCountry"
                              value={countriesList?.find((opt: any) => opt.value === value)}
                              options={countriesList}
                              error={errors.birthCountry?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <div className="pt-3 font-semibold">
                        <Translate id="visasManager:Marital Status" />{' '}
                        <span className="text-red">*</span>
                     </div>
                     <div className="flex gap-4 py-2">
                        <Controller
                           name="socialStates"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="SINGLE"
                                    {...register('socialStates', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                    defaultChecked
                                 />
                                 <span>
                                    <Translate id="visasManager:single" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="socialStates"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="MARRIED"
                                    {...register('socialStates', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:married" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="socialStates"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="DIVORCED"
                                    {...register('socialStates', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:divorced" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="socialStates"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="WIDOW"
                                    {...register('socialStates', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:widow" />
                                 </span>
                              </label>
                           )}
                        />
                        <Controller
                           name="socialStates"
                           control={control}
                           render={() => (
                              <label className="block w-full space-x-4 cursor-pointer rtl:space-x-reverse">
                                 <input
                                    type="radio"
                                    defaultValue="OTHER"
                                    {...register('socialStates', {
                                       required: translate({ id: 'validation:required' }),
                                    })}
                                 />
                                 <span>
                                    <Translate id="visasManager:other" />
                                 </span>
                              </label>
                           )}
                        />
                     </div>
                  </div>
               </form>
            </div>
         </div>
         <RequestFooter
            fwdBtnHandler={handleSubmit(submitForm)}
            cancelBtnHandler={() => router.back()}
            loading={loading}
            fwdBtnDisabled={disabled}
         />
      </>
   );
};
